import moment from 'moment';

/**
 * Pure helpers for the Ops → Crons surfaces (list + detail). Data lives in
 * models/useCronControls.js; these are display-time derivations only.
 *
 * Backend crons self-register their control row on their first tick (see
 * functions/utils/cronControl.js) — this catalog only guarantees a cron is
 * listed BEFORE that first tick, with the same defaults the backend compiled
 * in. A live control doc always wins over the catalog entry.
 */
export const KNOWN_CRONS = [
  {
    name: 'kioskSweepCron',
    description: 'Arms Always-on (kiosk) terminals every 5 minutes via the payer kiosk-sweep endpoint. OFF by default — enable to drive hardware.',
    schedule: 'every 5 minutes (Europe/Dublin)',
    defaultEnabled: false,
    overdueAfterMinutes: 20,
  },
  {
    name: 'credentialHealthCron',
    description: 'Credential health: live-client owners with no auth user (lockouts). OFF pending worklist rewrite.',
    schedule: 'Mondays 07:00 Europe/Dublin',
    defaultEnabled: false,
    overdueAfterMinutes: 8 * 24 * 60,
  },
];

export const toMoment = (ts) => (ts && typeof ts.toDate === 'function' ? moment(ts.toDate()) : null);

export const formatTs = (ts) => {
  const m = toMoment(ts);
  return m ? m.format('YYYY-MM-DD HH:mm') : null;
};

/** The switch semantics, mirrored from the backend: explicit choice wins, absence means default. */
export const effectiveEnabled = (row) => (row.enabled == null ? row.defaultEnabled === true : row.enabled === true);

/** Catalog rows guarantee presence; live control docs override them field by field. */
export const mergeWithCatalog = (catalog, controls) => {
  const byName = new Map(catalog.map((c) => [c.name, { ...c }]));
  controls.forEach((doc) => {
    byName.set(doc.name, { ...(byName.get(doc.name) || {}), ...doc });
  });
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const tsMs = (ts) => {
  const m = toMoment(ts);
  return m ? m.valueOf() : null;
};

/**
 * Health, derived at display time from stamps the ticks already write — no
 * extra records anywhere. Pure: pass nowMs for testability.
 *   unknown — never ticked (nothing to judge)
 *   overdue — silent past the cron's declared tolerance: the SCHEDULE is broken
 *             (deleted job, failed deploy); heartbeats fire even while OFF, so
 *             this holds for paused crons too
 *   failing — the last attempted run threw (lastErrorAt newer than lastSuccessAt)
 *   healthy — ticking, and the last attempt didn't fail
 */
export const deriveCronHealth = (row, nowMs) => {
  const lastBeatMs = Math.max(tsMs(row.lastRunAt) || 0, tsMs(row.lastSkippedAt) || 0) || null;
  if (!lastBeatMs) return { status: 'unknown' };
  if (row.overdueAfterMinutes && nowMs - lastBeatMs > row.overdueAfterMinutes * 60 * 1000) {
    return { status: 'overdue', sinceMs: lastBeatMs };
  }
  const errMs = tsMs(row.lastErrorAt);
  if (errMs && errMs > (tsMs(row.lastSuccessAt) || 0)) {
    return { status: 'failing', lastError: row.lastError || null };
  }
  return { status: 'healthy' };
};
